import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { workspaceApi, type CareEvent } from '../api/workspace';
import { Loading, PageError } from '../components/State';
import { openAttachment } from '../lib/attachment';
import { fmtDate, money } from '../lib/format';
import '../components/EventRow.css';
import './ShiftDetailsPage.css';

type NavState = { event?: CareEvent; youthName?: string } | null;

const STATUS_LABEL: Record<string, string> = {
  pending: 'Awaiting caregiver response',
  confirmed: 'Confirmed',
  declined: 'Declined by caregiver',
  cancelled: 'Cancelled',
};

export function ShiftDetailsPage() {
  const { eventId = '' } = useParams();
  const nav = useLocation().state as NavState;

  // Fast path: opened from a list, event came in on nav state.
  // Fallback: hard refresh / deep link — rebuild the schedule and find it.
  const needFallback = !nav?.event || nav.event.id !== eventId;
  const fallback = useQuery({
    queryKey: ['schedule'],
    queryFn: async () => {
      const clients = await workspaceApi.clients();
      const perClient = await Promise.all(
        clients.map(async (c) => {
          const evs = await workspaceApi.events(c.connectionId).catch(() => [] as CareEvent[]);
          return evs.map((e) => ({ ...e, youthName: c.youthSnapshot?.name ?? 'Youth' }));
        }),
      );
      return perClient.flat();
    },
    enabled: needFallback,
  });

  if (needFallback && fallback.isLoading) return <Loading />;

  const event: CareEvent | undefined = needFallback
    ? fallback.data?.find((e) => e.id === eventId)
    : nav?.event;
  const youthName =
    (needFallback
      ? (fallback.data?.find((e) => e.id === eventId) as { youthName?: string } | undefined)
          ?.youthName
      : nav?.youthName) ?? 'Youth';

  if (!event) {
    return (
      <div>
        <Link to="/schedule" className="yp__back">
          ‹ Schedule
        </Link>
        <PageError error={{ status: 404, message: 'Event not found.' }} />
      </div>
    );
  }

  return (
    <div className="sd">
      <Link to="/schedule" className="yp__back">
        ‹ Schedule
      </Link>
      <h1>{event.title}</h1>
      <span className={`evrow__status evrow__status--${event.status}`}>
        {STATUS_LABEL[event.status] ?? event.status}
      </span>

      <dl className="sd__grid">
        <Field label="Youth" value={youthName} />
        <Field label="Date" value={fmtDate(event.date)} />
        <Field label="Time" value={event.time || '—'} />
        <Field label="Location" value={event.location || '—'} />
        <Field
          label="Cost"
          value={event.isFree ? 'Free' : money(event.cost, event.currency)}
        />
        {event.confirmedAt && (
          <Field label="Confirmed" value={fmtDate(event.confirmedAt.split('T')[0])} />
        )}
      </dl>

      {event.notes && (
        <section className="sd__section">
          <h2>Notes</h2>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{event.notes}</p>
        </section>
      )}

      {event.formUrl && (
        <section className="sd__section">
          <h2>Form</h2>
          <a href={event.formUrl} target="_blank" rel="noopener noreferrer">
            {event.formUrl}
          </a>
        </section>
      )}

      {(event.hasAttachmentFile || event.attachmentUrl) && (
        <section className="sd__section">
          <h2>Attachment</h2>
          {event.hasAttachmentFile ? (
            <AttachmentButton eventId={event.id} name={event.attachmentName} />
          ) : (
            <a href={event.attachmentUrl} target="_blank" rel="noopener noreferrer">
              {event.attachmentName || event.attachmentUrl}
            </a>
          )}
        </section>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="sd__field">
      <dt className="muted">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function AttachmentButton({ eventId, name }: { eventId: string; name: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div>
      <button
        className="btn btn--ghost"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setErr(null);
          try {
            const payload = await workspaceApi.eventAttachment(eventId);
            openAttachment(payload);
          } catch {
            setErr('Could not load the attachment.');
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? 'Opening…' : `📎 ${name || 'Open attachment'}`}
      </button>
      {err && <p className="error-text">{err}</p>}
    </div>
  );
}
